/**
 * Session token storage. The backend issues a short-lived session JWT; this
 * module is the only place the client reads/writes it.
 *
 * BASELINE: stored in localStorage for simplicity. The design target is an
 * httpOnly cookie + refresh token (docs/specs/00-shared-contracts.md, public-client failure-mode
 * mitigation) — swap this module's implementation when that lands; callers
 * (client interceptor, useAuth, useLogin) do not change.
 */
const TOKEN_KEY = 'mnr.accessToken';

/**
 * View as user (MYN-335, §51): an admin's view-as session lives in ONE tab.
 * Its token is per tab (sessionStorage, like the shared-profile token), so the
 * admin's own console session in localStorage is never replaced, and a tab
 * that was ever a view-as tab never falls back to that admin session.
 */
const VIEW_AS_TOKEN_KEY = 'mnr.viewAsToken';
const VIEW_AS_TAB_KEY = 'mnr.viewAsTab';

/** True in a tab opened for a view-as session, for as long as the tab lives. */
export function isViewAsTab(): boolean {
  try {
    return sessionStorage.getItem(VIEW_AS_TAB_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Make this tab a view-as tab carrying `token`.
 *
 * @returns false when per-tab storage is unavailable, so the caller can refuse
 *          to run the session rather than fall back to the admin's own token.
 */
export function startViewAsTab(token: string): boolean {
  try {
    sessionStorage.setItem(VIEW_AS_TAB_KEY, '1');
    sessionStorage.setItem(VIEW_AS_TOKEN_KEY, token);
    return true;
  } catch {
    return false;
  }
}

export function getAccessToken(): string | null {
  try {
    if (isViewAsTab()) return sessionStorage.getItem(VIEW_AS_TOKEN_KEY);
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAccessToken(token: string): void {
  try {
    // Signing in for real in a former view-as tab makes it an ordinary tab again.
    sessionStorage.removeItem(VIEW_AS_TAB_KEY);
    sessionStorage.removeItem(VIEW_AS_TOKEN_KEY);
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // storage unavailable (private mode / quota) — session stays in-memory only
  }
}

/** In a view-as tab this drops only that tab's token: the admin's own session is another tab's. */
export function clearAccessToken(): void {
  try {
    if (isViewAsTab()) sessionStorage.removeItem(VIEW_AS_TOKEN_KEY);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // no-op
  }
}
